import { BSLEmploymentStatus, Employee as EmployeeType, Gender, JobStatus } from '@cbo/shared-library';
import { ActivationTypes } from '@cbo/shared-library/types/activation';
import { BaseGridRow } from '../components/DataGrid/DataGrid';
import { BaseReportDataRequest } from '../../api/sales/requests';

export interface BaseEntity {
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  deletedAt: string | null;
  deletedBy: string | null;
}

export interface EmployeeContactInformation {
  emailAddress: string;
  lastName: string;
  firstName: string;
  nickname: string;
  phoneNumber: string;
}

export interface UpdateEmployeeContactInfoRequestSchema {
  contact: EmployeeContactInformation;
}

// TODO remove orgId or change the patch DTO as orgId is required according to the service
// https://github.com/ncrvoyix-swt-hospitality/cbo-employee-information-service/blob/932a2bd0fdab5003f7afb8d7f471556396183aaf/src/employees/employees.service.ts#L269

export interface EmployeeCertificationsDetailsInformation extends BaseEntity {
  id: string;
  certificationId: string;
  nepOrganization: string;
  certificationName: string;
  certificationReference: string;
  certificationExpirationDate: Date;
  isExcluded: boolean;
}

export interface EmployeePersonalInformation {
  address1: string;
  address2: string;
  city: string;
  state: string;
  county: string;
  postalCode: string;
  country: string;
  picture?: string;
}

export interface EmployeeContactDetailsInformation extends EmployeeContactInformation, EmployeePersonalInformation {}

export interface EmployeeDetailsInfo extends BaseEntity {
  contact: EmployeeContactDetailsInformation;
  certifications: EmployeeCertificationsDetailsInformation[];
  birthDate: string | undefined;
  employeeId: string;
  nepOrganization: string;
}

export interface SelfJobProfile {
  jobProfileId: string;
  jobId: string;
  jobName: string | undefined;
}

export interface UpdateEmployeeCertificationSchema {
  certificationName?: string;
  certificationReference?: string;
  certificationExpirationDate?: string;
  excluded?: boolean;
  formCompletionStatus?: EmployeeType.EmployeeFormCompletionStatus;
}

export interface UpdateEmployeeCertificationInfoSchema {
  certificationName?: string;
  certificationReference?: string;
  certificationExpirationDate?: string;
  isExcluded?: boolean;
}

// TODO LaborGroupSchema move to shared library
export interface LaborGroupSchema {
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  deletedAt: Date | null;
  deletedBy: Date | null;
  id: string;
  name: string;
  description: string;
  nepEnterpriseOrganization: string;
  nepEnterpriseUnit: string;
  jobCodes: string[];
}

export interface UpdateEmployeePersonalInfoRequestSchema {
  birthDate: string;
  contact: {
    gender?: Gender;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

export interface UpdateEmployeePermissionsSchema {
  posEmployeeId?: string;
  useMagneticCards?: boolean;
  prefersQwertyKeyboard?: boolean;
  useFingerPrintScannerForClockIn?: boolean;
  useFingerPrintScannerForLoginAndManagerApproval?: boolean;
}

export interface UpdateEmployeeDetailsSchema {
  ncrUserId?: string;
  exportId?: string;
  posEmployeeId?: string;
  homeEnterpriseUnitId?: string;
  doNotImportPosTimePunches?: boolean;
  employeeClassification?: JobStatus;
  posConfigs?: UpdateEmployeePermissionsSchema;
  birthDate?: Date | null;
  lastName?: string;
  firstName?: string;
  emailAddress?: string;
  phoneNumber?: string;
  nickName?: string;
  deviceLogin?: string | null;
}

export interface EmployeeSalesPerformanceCommonRequest extends BaseReportDataRequest {
  jobcodes?: string[];
  dayParts?: string[];
}

export interface EmployeeSalesPerformanceKpiCardsResponse {
  salesPerLaborHour: number;
  salesPerPerson: number;
  checkTimeMinutes: number;
}

export interface EmployeeSalesPerformanceGridDataRow extends BaseGridRow {
  name: string;
  employeePosId: string;
  jobcode: string;
  dayPart: string;
  perPersonAverage: number;
  totalNetSales: number;
  numberOfGuests: number;
  numberOfChecks: number;
  averageCheckTimeMinutes: number;
  compAmount: number;
  voidAmount: number;
  salesPerLaborHour: number;
}

export interface EmployeeSalesPerformanceGridDataResponse {
  results: EmployeeSalesPerformanceGridDataRow[];
}

export interface OvertimeThresholdGridDataRow extends BaseGridRow {
  id: number;
  name: string;
  posEmployeeId: string;
  actualHours: number;
  remainingHours: number;
  expectedTotalHours: number;
  remainingHoursBeforeOvertime: number;
  nextShift: string;
}
export interface OvertimeThresholdGridDataResponse {
  results: OvertimeThresholdGridDataRow[];
}

export interface OvertimeThresholdCommonRequest {
  startDate: string;
  endDate: string;
  siteIds: string[];
  hourThresholdLowerRange?: number;
  hourThresholdHigherRange?: number;
}

export interface OvertimeThresholdKpiCardsResponse {
  exceedingOvertime: number;
  approachingOvertimeMiddle: number;
  approachingOvertimeLower: number;
}

export interface JobcodeHistoryGridRequest {
  siteIds: string[];
  employeeId: string;
}

export interface JobcodeHistoryGridRow extends BaseGridRow {
  jobProfileId: string;
  jobId: string;
  jobName?: string;
  payRate: number;
  effectiveDate: string;
  modifiedDate: string;
  modifiedBy: string;
  posJobCodeId?: string;
}

export interface JobcodeHistoryGridResponse {
  response: JobcodeHistoryGridRow[];
}

export interface EmployeeSitesGridRow extends BaseGridRow {
  enterpriseUnitId: string;
  name: string;
  enterpriseTypeName: string;
}

export interface GetUserSitesResponse {
  lastPage: boolean;
  pageNumber: number;
  totalPages: number;
  totalResults: number;
  pageContent: EmployeeSitesGridRow[];
}

export interface UpdateResponse {
  body: {
    message: string;
    rows_affected: number;
  };
}

export interface UpdateUserStatusResponse {
  result: {
    username: string;
    displayUsername: string;
    status: BSLEmploymentStatus;
  };
}

export interface EmployeeBreakReportCommonRequestSchema {
  startDate: string;
  endDate: string;
  siteIds?: string[];
  employees?: string[];
}

export interface EmployeeBreakReportKpiCardsDataResponse {
  overdueBreaks: number;
  upcomingBreaks: number;
}

export interface EmployeeBreakGridDataRow extends BaseGridRow {
  id: number;
  date: string;
  site: string;
  name: string;
  posEmployeeId: string;
  jobcode: string;
  clockIn: string;
  breakType: string;
  breakDueBy: string;
}

export interface EmployeeBreakReportGridDataResponse {
  results: EmployeeBreakGridDataRow[];
}

export interface EmployeeTipGridDataRow extends BaseGridRow {
  id: number;
  name: string;
  posEmployeeId: string;
  netSales: number;
  chargeSales: number;
  totalDeclaredTips: number;
  chargeTipAmount: number;
  salesTipPercent: number;
  chargeTipsPercent: number;
  nonChargeSales: number;
  nonChargeTips: number;
  nonChargeTipsPercent: number;
  salesWithAutoGratuity: number;
  autoGratuityAmount: number;
}

export interface ExportFileReportGridResponse {
  signedUrl: string;
}

export type TotalChargeTipsData = {
  totalChargeTips: number;
  totalChargesales: number;
  totalChargeTip: number;
  date: string;
};

export type TotalTipsData = {
  totalTips: number;
  totalSales: number;
  totalTip: number;
  date: string;
};

export type Top20UsageChartsData = {
  itemName: string;
  top20: number | null;
  bottom10: number | null;
};

export interface EmployeeTipKPICardsAndChartsData {
  totalChargeTips: TotalChargeTipsData[];
  totalTips: TotalTipsData[];
  totalDeclaredTips: number;
  totalAutoGratuity: number;
}

export interface EmployeeTipReportGridDataResponse {
  results: EmployeeTipGridDataRow[];
}

export interface IntervalSalesAndLaborReportCommonRequestSchema {
  startDate: string;
  endDate: string;
  siteIds: string[];
  jobCodes: string[];
  laborReportingGroup: string[];
  granularity?: number;
}

export interface IntervalSalesAndLaborGridDataRow extends BaseGridRow {
  hierarchy: string[];
  id: number;
  dayPart: string;
  time: Date;
  netSalesAmount: number;
  forecastedSalesAmount: number;
  salesVariance: number;
  actualHours: number;
  scheduleHours: number;
  laborVariance: number;
  laborCostAmount: number;
  laborPercentage: number;
  salesPerLaborHour: number;
  numberOfGuests: number;
  numberOfChecks: number;
}

export interface IntervalSalesAndLaborGridDataResponse {
  response: IntervalSalesAndLaborGridDataRow[];
}

export interface IntervalSalesAndLaborKPIResponse {
  totalNetSalesAmount: number;
  totalNetSalesComparedToForecastPercentage: number;
  totalLaborHours: number;
  totalLaborHoursComparedToScheduledPercentage: number;
  averageSalesPerLaborHour: number;
}

export interface IntervalSalesLaborAverageSales {
  id: number;
  dayPart: string;
  averageSalesPerLaborHour: number;
  netSales: number;
  laborHours: number;
}

export interface IntervalSalesAndLaborAverageSalesResponse {
  response: IntervalSalesLaborAverageSales[];
}

export interface IntervalSalesAndLaborResponse {
  response: {
    grid: IntervalSalesAndLaborGridDataRow[];
    avgSales: IntervalSalesLaborAverageSales[];
    kpi: IntervalSalesAndLaborKPIResponse;
  };
}

export interface GenericPayrollExportGridDataResponse {
  employeeName: string;
  employeePOSId: string;
  jobCode: string;
  payRate: number;
  totalNetSales: number;
  employeeSalesAmountPercent: number;
  regularHoursWorked: number;
  overtimeHoursWorked: number;
  dailyOvertimeHoursWorked: number;
  premiumHoursWorked: number;
  declaredTips: number;
  employeeTipsAmountPercent: number;
}

export interface ApproachingACAHourlyThresholdGridDataRequest {
  startDate: string;
  endDate: string;
  siteIds: string[];
  jobType?: string[];
  employeeStatus?: string[];
  hourThresholdLowerRange?: number;
  hourThresholdHigherRange?: number;
}

export interface ApproachingACAHourlyThresholdOutput extends BaseGridRow {
  siteName: string;
  employeeName: string;
  employeePosId: string;
  jobType: string;
  employeeStatus: string;
  actualHours: number;
  remainingHours: number;
  expectedTotalHours: number;
}

export interface ApproachingACAHourlyThresholdGridDataResponse {
  results: ApproachingACAHourlyThresholdOutput[];
}

export interface ApproachingACAThresholdAverageHoursWorkedRow extends BaseGridRow {
  id: number;
  siteName: string;
  employeeName: string;
  employeePosId: string;
  averageHoursWorked: number;
  actualHours: Record<string, number | null>;
}

export interface ApproachingACAThresholdAverageHoursWorkedGridResponse {
  results: ApproachingACAThresholdAverageHoursWorkedRow[];
}

export interface EmployeePayrollDto extends BaseReportDataRequest {
  jobCodeNames?: string[];
}

export interface EmployeePayrollDetailExportDto extends EmployeePayrollDto {
  payrollDetailColumns: string[];
}

export interface EmployeePayrollGridResults extends BaseGridRow {
  id: string;
  name: string;
  employeePosId: string;
  jobcode: string;
  payRate: number;
  totalHoursWorked: number;
  totalWages: number;
  regularHoursWorked: number;
  regularWages: number;
  overtimeHoursWorked: number;
  overtimeWages: number;
  premiumHoursWorked: number;
  premiumOvertimeWages: number;
  totalNetSales: number;
  declaredTips: number;
  totalTips: number;
  tipPercent: number;
  tipMakeupPay: number;
}

export interface EmployeePayrollGridResponse {
  results: EmployeePayrollGridResults[];
}
export interface EmployeePayrollJobcodeItemData {
  jobcodeName: string;
  percentOfTotal: number;
  wageTotal: number;
}

export interface EmployeePayrollKPICardData {
  laborPercentOfCost: EmployeePayrollJobcodeItemData[];
  totalLiability: number;
}
export interface EmployeePayrollWithOvertimeKpiCards extends EmployeePayrollKPICardData {
  totalOvertimeHours?: number | undefined;
}

export interface EmployeePayrollDetailGridDataRow extends BaseGridRow {
  id: number;
  name: string;
  employeePosId: string;
  businessDate: string;
  jobcode: string;
  payRate: number;
  clockIn: string;
  clockOut: string;
  clockEventType: string;
  breakType: string;
  regularHoursWorked: number;
  regularWages: number;
  overTimeHoursWorked: number;
  overTimeWages: number;
  premiumHoursWorked: number;
  premiumOverTimeWages: number;
  penaltyWages: number;
  totalHoursWorked: number;
  totalWages: number;
  totalNetSales: number;
  totalTips: number;
  totalDeclaredTips: number;
  totalDeclaredTipsPercent: number;
}

export interface EmployeePayrollDetailDataGridResponse {
  results: EmployeePayrollDetailGridDataRow[];
}

export interface OvertimeThresholds {
  lowerThreshold: number;
  middleThreshold: number;
  upperThreshold: number;
}

export interface ActualHoursWorked {
  lower?: number;
  higher?: number;
}

export interface Job extends BaseEntity {
  jobId: string;
  laborGroupId?: string;
  nepOrganization?: string;
  name: string;
  isTipped: boolean;
  description?: string;
  posJobCodeId?: string;
  excludeFromPayrollReport?: boolean;
  excludeHoursAndDollarsFromScheduleMetrics?: boolean;
  jobProfileCount?: number;
  isActive: boolean;
  /** Indicates if this job contributes to tip share calculations based on employee sales */
  eligibleForTipContribution?: boolean;
  /** Indicates if this job participates in tip share calculations */
  eligibleForTipShare?: boolean;
  /** Labor group used specifically for tip share distribution */
  tipShareLaborGroupId?: string | null;
  cfcEuid?: string;
}

export interface UpdateJobDto {
  laborGroupId?: string;
  name: string;
  description?: string;
  posJobCodeId?: string;
  isActive: boolean;
  isTipped: boolean;
  excludeFromPayrollReport?: boolean;
  excludeHoursAndDollarsFromScheduleMetrics?: boolean;
  eligibleForTipContribution?: boolean;
  eligibleForTipShare?: boolean;
  tipShareLaborGroupId?: string | null;
}

export interface GetPaginatedJobsRequest {
  pagenum?: number;
  pagesize?: number;
  ids?: string[];
  includeJobProfileCount?: boolean;
  enterpriseUnit?: string;
}

export interface ChangeJobActivationStatusDto {
  activationStatus: boolean;
}

export interface LaborGroup extends BaseEntity {
  laborGroupId: string;
  nepOrganization: string;
  name: string;
  description: string;
  jobs: [
    {
      jobId: string;
    }
  ];
}

export interface JobProfileHistory extends BaseEntity {
  performanceRating: number;
  startDate: string;
  endDate: string;
  payRate: number;
}

export interface JobProfile extends BaseEntity {
  jobProfileId: string;
  nepOrganization: string;
  employeeId: string;
  jobId: string;
  performanceRating: number;
  startDate: string;
  endDate?: string;
  payRate: number;
  history?: JobProfileHistory[];
  cfcJobEuid?: string;
  posJobCodeId?: string;
  isPrimary?: boolean;
}

export interface UpdateJobProfileDto {
  performanceRating?: number;
  startDate?: string;
  isPrimary?: boolean;
  newPrimaryJobProfileId?: string;
  endDate?: string;
  payRate?: number;
}

export interface ExportGenericPayrollDto {
  startDate?: string;
  endDate?: string;
  siteIds?: string[];
}

export enum EmploymentStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  TERMINATED = 'TERMINATED',
  LEAVE_OF_ABSENCE = 'LEAVE_OF_ABSENCE',
  INACTIVE = 'INACTIVE',
}

export enum EuAssignmentStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
}

export interface EmploymentHistoryIdResponse {
  employmentHistoryId: string;
}

export interface EmploymentHistory extends BaseEntity {
  employmentHistoryId?: string;
  nepOrganization?: string;
  employmentStatus: EmploymentStatus;
  statusName: string;
  reason: string;
  effectiveDate?: Date | null;
  eligibleForRehire?: string | null;
  expectedReturnDateFromLeaveOfAbsence?: Date | string | null;
}

export interface POSConfigs {
  useMagneticCards: boolean;
  prefersQwertyKeyboard: boolean;
  useFingerPrintScannerForClockIn: boolean;
  useFingerPrintScannerForLoginAndManagerApproval: boolean;
}

export interface ContactInformationDetails {
  emailAddress: string;
  firstName: string;
  lastName: string;
  nickname: string;
  phoneNumber: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  county?: string;
  postalCode?: string;
  country?: string;
}

export interface Employee extends BaseEntity {
  id?: string;
  employeeId: string;
  posEmployeeId: string;
  nepOrganization: string;
  ncrUserId: string;
  homeEnterpriseUnitId: string;
  posConfigs: POSConfigs;
  doNotImportPosTimePunches: boolean;
  employeeClassification: JobStatus;
  employmentStatus: EmploymentStatus;
  employeeEuGrantStatus?: EuAssignmentStatus;
  birthDate: Date | null;
  employmentHistory: EmploymentHistory[];
  contact: ContactInformationDetails;
  certifications: EmployeeCertificationsDetailsInformation[];
  exportId: string;
  jobProfileCount: number;
  deviceLogin?: string;
  cfcEuid?: string;
}

interface BaseEmployeeHistorySchema {
  employmentStatus: string;
  statusName: string;
  reason?: string;
  effectiveDate: Date | null | undefined;
  eligibleForRehire?: boolean;
  expectedReturnDateFromLeaveOfAbsence?: Date | null;
}
export type UpdateEmployeeHistorySchema = BaseEmployeeHistorySchema;

interface BaseEmployeeRequestSchema {
  ncrUserId?: string;
  exportId?: string;
  posEmployeeId?: string;
  homeEnterpriseUnitId?: string;
  doNotImportPosTimePunches?: false;
  employeeClassification?: JobStatus;
  posConfigs?: string;
  birthDate?: string;
  lastName?: string;
  firstName?: string;
  emailAddress?: string;
  phoneNumber?: string;
  nickname?: string;
}

export type UpdateEmployeeRequestSchema = BaseEmployeeRequestSchema;

export interface CreateEmployeeRequestSchema extends BaseEmployeeRequestSchema {
  employmentHistory?: BaseEmployeeHistorySchema[];
}

export type EmployeeSortBy = 'name' | 'missingRequiredFields' | 'posEmployeeId' | '';
export type EmployeeSortOrder = 'ASC' | 'DESC' | '';

export interface GetEmployeesRequestSchema {
  pagenum?: number;
  pagesize?: number;
  employmentStatus?: string | string[];
  posEmployeeIds?: string | string[];
  employeeIds?: string[];
  includeJobProfileCount?: string;
  enterpriseUnit?: string;
  fullEmploymentHistory?: boolean;
  sortOrder?: EmployeeSortOrder;
  sortBy?: EmployeeSortBy;
  filter?: string[];
}

export interface GetJobProfilesRequestSchema {
  pagenum?: number;
  pagesize?: number;
  jobProfileIds?: string[];
  includeHistory?: boolean;
}

export interface CreateSelfEmployeeRequestSchema {
  ncrUserId: string;
  lastName: string;
  firstName: string;
  emailAddress: string;
}

export interface CreateSelfEmployeeResponse {
  employeeId: string;
}

export type CreateEmployeeResponse = CreateSelfEmployeeResponse;
export interface ContactInformation extends BaseEntity {
  emailAddress: string;
  lastName: string;
  firstName: string;
  nickname: null | string;
  phoneNumber: string;
  formCompletionStatus: EmployeeType.EmployeeFormCompletionStatus;
}
export interface PersonalInformation extends BaseEntity {
  birthDate: string;
  gender: Gender;
  address1: string;
  address2: string;
  city: string;
  state: string;
  county: string;
  postalCode: string;
  country: string;
  picture?: string;
  formCompletionStatus: EmployeeType.EmployeeFormCompletionStatus;
}

export interface EmployeeCertification extends BaseEntity {
  id: string;
  employee?: string;
  formCompletionStatus: EmployeeType.EmployeeFormCompletionStatus;
  certificationName?: string;
  certificationReference?: string;
  certificationExpirationDate?: Date;
  excluded?: boolean;
}

export interface EmployeeJobCodeAssignment extends BaseEntity {
  id: string;
  employeeId: string;
  jobCodeId: string;
  employeeJobCodeRating: number;
}

export enum EmploymentReason {
  NEW_HIRE = 'NEW_HIRE',
  REHIRE = 'REHIRE',
  RETURN_FROM_LEAVE = 'RETURN_FROM_LEAVE',
  TRANSFERRED_IN = 'TRANSFERRED_IN',
}

// TODO: Remove/update enum once LRE is available
export enum LaborWarning {
  PART_TIME_CRITICAL = 'PART_TIME_CRITICAL',
  OVERTIME_WARNING = 'OVERTIME_WARNING',
  OVERTIME_CRITICAL = 'OVERTIME_CRITICAL',
}

export interface PayRate extends BaseEntity {
  id?: string;
  nepEnterpriseUnit?: string;
  nepEnterpriseOrganization?: string;
  payRate?: number | null;
  activationStatus?: ActivationTypes;
  activationReason?: string;
  activationDetails?: string;
  activationEffectiveDate?: string;
}

export interface AddPayRateRequestBody {
  employeeConfigurationId?: string;
  payRate?: number;
  activationStatus?: ActivationTypes;
  activationReason?: string;
  activationDetails?: string;
  activationEffectiveDate?: Date;
}

export interface PatchPayRateRequestBody {
  payRate?: number;
  activationStatus?: string;
  activationReason?: string;
  activationDetails?: string;
  activationEffectiveDate?: Date;
}

export interface EmployeeRowDto {
  employeeId: string;
  jobId: string;
  actualShiftId?: string;
  tipShareEarned: number;
  otherEarnings: number;
}

export enum DistributionMethod {
  BY_HOURS = 'by_hours',
  BY_JOB = 'by_job',
  BY_LABOR_GROUP = 'by_labor_group',
}

export interface ManualSaveTipDistributionDto {
  businessDate: string;
  employees: EmployeeRowDto[];
  totalTipShareEarned: number;
  totalOtherEarnings: number;
}
export interface AutomaticSaveTipDistributionDto {
  businessDate: string;
  tipShareShiftId: string;
  tipPoolAmount: number;
  distributionMethod: DistributionMethod;
  jobAllocations?: { jobId: string; percent: number }[];
  laborGroupAllocations?: { laborGroupId: string; percent: number }[];
  employees: EmployeeRowDto[];
}

/**
 * Union type: Either manual or automatic save mode
 */
export type SaveTipDistributionDto = ManualSaveTipDistributionDto | AutomaticSaveTipDistributionDto;

export interface EmployeeTipRecord {
  employeeId: string;
  jobId: string;
  shiftId: string;
  tipShareEarned: number;
  otherEarnings: number;
}

export interface DistributionSummary {
  employeeCount: number;
  totalTipShare: number;
  totalOtherEarnings: number;
}

export interface SaveTipDistributionResponseDto {
  success: boolean;
  message?: string;
  data?: {
    businessDate: string;
    nepOrganization: string;
    nepEnterpriseUnit: string;
    employeeTips?: EmployeeTipRecord[];
    summary: DistributionSummary;
  };
}

export interface EmployeeTipShareRecord {
  id: string;
  businessDate: Date;
  employeeId: string;
  jobId: string;
  actualShiftId?: string | null;
  laborGroupId?: string | null;
  tipShareEarned: number;
  otherEarnings: number;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
}

/**
 * Single tip share shift distribution with its employees
 * Used when returning multiple shifts for automatic mode without filter
 */
export interface TipShareShiftDistribution {
  tipShareShiftId: string;
  distributionMethod: DistributionMethod;
  tipPoolAmount: number;
  employees: EmployeeTipShareRecord[];
  summary: DistributionSummary;
  isFinalized: boolean;
  finalizedAt: string | null;
  finalizedBy: string | null;
}

/**
 * GET /distribute/:date response
 *
 * Response varies based on distribution mode and filters:
 *
 * 1. Automatic mode without tipShareShiftId filter:
 *    Returns all shifts for the date in `tipShareShifts` array
 *
 * 2. Automatic mode with tipShareShiftId filter:
 *    Returns single shift in `tipShareShifts` array (one element)
 *
 * 3. Manual mode:
 *    Returns all employees and summary for the entire date
 */
export interface GetTipDistributionResponseDto {
  nepOrganization: string;
  nepEnterpriseUnit: string;
  businessDate: string;
  // Automatic mode: shifts array (can be empty, one, or many elements)
  tipShareShifts?: TipShareShiftDistribution[];
  // Manual mode: all employees for the date
  employees?: EmployeeTipShareRecord[];
  summary?: DistributionSummary;
}

// Tip Income Report — manual distribution summary returned by asm-tip-service
export interface TipIncomeReportRow {
  employeeId: string;
  employeeName: string;
  tipsDeclared: number;
  tipShareEarned: number;
  chargeTips: number;
  tippableSales: number;
}

// TipIncomeReportResponseDto is a plain array — backend returns TipIncomeReportRow[] directly
export type TipIncomeReportResponseDto = TipIncomeReportRow[];

// Phase 2: Calculate Tip Distribution DTOs
export interface JobAllocationDto {
  jobId: string;
  percent: number; // 0-100 range (NBO parity)
}

export interface LaborGroupAllocationDto {
  laborGroupId: string;
  percent: number; // 0-100 range (NBO parity)
}

export interface CalculateTipDistributionRequestDto {
  businessDate: string; // YYYY-MM-DD
  tipShareShiftId: string;
  tipPoolAmount: number; // Tip pool amount in dollars
  distributionMethod: DistributionMethod;
  jobAllocations?: JobAllocationDto[];
  laborGroupAllocations?: LaborGroupAllocationDto[];
}

export interface TipShareEmployeeLineItem {
  employeeId: string;
  jobId: string;
  actualShiftId?: string | null;
  minutes?: number; // Worked minutes for employee
  hours?: number; // Convenience field (derived from minutes)
  tipShareEarned: number; // Distributed amount in dollars
  laborGroupId?: string | null;
}

export interface TipShareCalcResult {
  nepOrganizationId: string;
  nepEnterpriseUnitId: string;
  businessDate: string; // YYYY-MM-DD
  tipShareShiftId: string;
  distributionMethod: string; // 'by_hours' | 'by_job' | 'by_labor_group'
  tipPoolAmount: number;
  employees: TipShareEmployeeLineItem[];
}

export interface JobDistributionSummary {
  jobId: string;
  minutes: number; // Minutes worked across all employees
  tipAmount: number; // Allocated/earned tip amount for job
}

export interface LaborGroupDistributionSummary {
  laborGroupId: string;
  minutes: number;
  tipAmount: number;
}

export interface CalculateTipDistributionResponseDto {
  result: TipShareCalcResult;
  aggregates?: {
    byJob?: JobDistributionSummary[];
    byLaborGroup?: LaborGroupDistributionSummary[];
  };
  canFinalize: boolean; // Whether distribution can be finalized
  isFinalized: boolean; // Whether distribution is already finalized
}

export interface FinalizationRequestDto {
  businessDate: string; // YYYY-MM-DD
  tipShareShiftId: string;
}

export interface FinalizationResponseDto {
  nepOrganization: string;
  nepEnterpriseUnit: string;
  businessDate: string;
  tipShareShiftId: string;
  isFinalized: boolean;
  finalizedAt: string | null;
  finalizedBy: string | null;
}

// Tip Share Types
export interface TipShareDistributionConfig {
  id?: string; // Distribution config ID (for updates)
  distributionType?: string; // 'automatic' | 'manual'
  distributionMethod?: string; // 'by_job' | 'by_labor_group' (only for automatic)
  recalculateOnPosImport?: boolean;
  autoBalanceGridDistribution?: boolean; // Only for manual
  isActive?: boolean; // Whether the distribution configuration is active
}

export interface TipShareContributionConfig {
  id?: string; // Contribution config ID (for updates)
  isActive?: boolean;
  contributionPercentage?: number;
  includeComps?: boolean;
  includePromotions?: boolean;
  includeTax?: boolean;
}

// Tip Share Shifts Types
export interface TipShareShift {
  id: string;
  nepEnterpriseUnit: string | null; // null = org-level, UUID = site-level
  nepOrganization: string;
  name: string;
  description: string | null;
  startTime: string;
  endTime: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface CreateTipShareShiftDto {
  name: string;
  description?: string;
  startTime: string;
  endTime: string;
}

export interface UpdateTipShareShiftDto {
  name?: string;
  description?: string;
  startTime?: string;
  endTime?: string;
}

export interface TipShareShiftRow {
  [key: string]: TipShareShift | string | undefined;
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  shift: TipShareShift;
}

export enum BannerType {
  PRIMARY_JOB_MIGRATED = 'PRIMARY_JOB_MIGRATED',
  EMAIL_REQUIRED = 'EMAIL_REQUIRED',
}

export enum BannerEntityType {
  EMPLOYEE = 'EMPLOYEE',
}

export interface BannerStateQueryParams {
  entityType: BannerEntityType;
  entityId: string;
  bannerType: BannerType;
}

export interface BannerStateViewDto {
  bannerType: BannerType;
  entityType: BannerEntityType;
  entityId: string;
  dismissCount: number;
  maxDismissals: number;
  isVisible: boolean;
}

export interface DismissBannerDto {
  entityType: BannerEntityType;
  entityId: string;
  bannerType: BannerType;
}

export interface DismissBannerResponseDto {
  success: boolean;
}
